import { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Receipt, AlertTriangle, Download, Settings, ChevronRight, IndianRupee, Clock, FileSpreadsheet } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function Billing() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientRates, setClientRates] = useState([]);
  const [services, setServices] = useState([]);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview | client-report

  useEffect(() => { fetchInitial(); }, []);

  const fetchInitial = async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([
        api.get('/clients'),
        api.get('/services')
      ]);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const fetchRates = async (clientId) => {
    try {
      const res = await api.get(`/billing/rates/${clientId}`);
      setClientRates(res.data);
    } catch { toast.error('Failed to load rates'); }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedClientId) params.append('client_id', selectedClientId);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const url = activeTab === 'client-report' && selectedClientId
        ? `/billing/client-report/${selectedClientId}?${params}`
        : `/billing/report?${params}`;
      const res = await api.get(url);
      setReport(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate report'); }
    finally { setReportLoading(false); }
  };

  const handleDownload = async () => {
    if (!selectedClientId) { toast.error('Select a client first'); return; }
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await api.get(`/billing/client-report/${selectedClientId}/download?${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const clientName = clients.find(c => c.id === selectedClientId)?.company || 'client';
      link.setAttribute('download', `billing_${clientName.replace(/\s+/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch { toast.error('Failed to download report'); }
  };

  const openRateDialog = async () => {
    if (!selectedClientId) { toast.error('Select a client first'); return; }
    await fetchRates(selectedClientId);
    setRateDialogOpen(true);
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8 min-h-full bg-slate-50">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="billing-title">Billing</h1>
          <p className="text-slate-500">Task-level billing per client</p>
        </div>
      </div>

      {/* Controls Row */}
      <Card className="border-slate-200 shadow-sm mb-6" data-testid="billing-controls">
        <CardContent className="p-4">
          <div className="flex items-end gap-3 md:gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Client</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger data-testid="billing-client-select"><SelectValue placeholder="All Clients" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_clients">All Clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="billing-date-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="billing-date-to" />
            </div>
            <Button onClick={fetchReport} className="bg-[#5B6FD6] hover:bg-[#4a5bc2]" data-testid="generate-report-btn">
              Generate Report
            </Button>
            {selectedClientId && selectedClientId !== 'all_clients' && (
              <>
                <Button variant="outline" onClick={openRateDialog} data-testid="manage-rates-btn">
                  <Settings className="h-4 w-4 mr-2" /> Rates
                </Button>
                <Button variant="outline" onClick={handleDownload} data-testid="download-report-btn">
                  <Download className="h-4 w-4 mr-2" /> Excel
                </Button>
              </>
            )}
          </div>

          {/* Tab toggle */}
          <div className="flex gap-2 mt-4 border-t border-slate-100 pt-3">
            <button onClick={() => setActiveTab('overview')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-[#5B6FD6] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="tab-overview">
              Overview
            </button>
            <button onClick={() => setActiveTab('client-report')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === 'client-report' ? 'bg-[#5B6FD6] text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid="tab-client-report">
              Client Report
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Warnings */}
      {report?.warnings?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 shadow-sm mb-4" data-testid="billing-warnings">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Rate Warnings</span>
            </div>
            <div className="space-y-1">
              {report.warnings.map((w, i) => {
                const wKey = typeof w === 'string' ? w : (w.id || `${w.member_id || ''}-${w.message}`);
                return (
                <p key={wKey} className="text-sm text-amber-700" data-testid={`warning-${i}`}>
                  {typeof w === 'string' ? w : w.message}
                </p>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {report?.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          {[
            { label: 'Total Hours', value: report.summary.total_hours, icon: Clock, color: 'text-[#5B6FD6]', bg: 'bg-[#5B6FD6]/10', suffix: 'h' },
            { label: 'Billable Hours', value: report.summary.billable_hours, icon: Clock, color: 'text-green-600', bg: 'bg-green-50', suffix: 'h' },
            { label: 'Non-Billable', value: report.summary.non_billable_hours, icon: Clock, color: 'text-slate-500', bg: 'bg-slate-100', suffix: 'h' },
            { label: 'Total Amount', value: `₹${report.summary.total_amount.toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-emerald-600', bg: 'bg-emerald-50', suffix: '' },
          ].map(c => (
            <Card key={c.label} className="border-slate-200 shadow-sm" data-testid={`summary-${c.label.toLowerCase().replace(/ /g, '-')}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center`}><c.icon className={`h-5 w-5 ${c.color}`} /></div>
                <div><p className="text-xs text-slate-500">{c.label}</p><p className="text-xl font-bold text-slate-900">{c.value}{c.suffix}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Report Content */}
      {reportLoading && <div className="text-center py-12 text-slate-400">Generating report...</div>}

      {!reportLoading && report && activeTab === 'overview' && <OverviewTable data={report} />}
      {!reportLoading && report && activeTab === 'client-report' && <ClientReportView data={report} />}

      {!report && !reportLoading && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">Select filters and click "Generate Report" to view billing data</p>
          </CardContent>
        </Card>
      )}

      {/* Rate Management Dialog */}
      <RateDialog
        open={rateDialogOpen}
        onClose={() => setRateDialogOpen(false)}
        clientId={selectedClientId}
        clientName={clients.find(c => c.id === selectedClientId)?.company || ''}
        services={services}
        rates={clientRates}
        onSave={async (rates) => {
          try {
            await api.put(`/billing/rates/${selectedClientId}`, { rates });
            toast.success('Rates saved');
            setRateDialogOpen(false);
          } catch { toast.error('Failed to save rates'); }
        }}
      />
    </div>
  );
}


// --- Overview Table (all clients, all tasks, all employees) ---
function OverviewTable({ data }) {
  if (!data?.entries?.length) {
    return <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-400">No billing entries found for the selected filters</CardContent></Card>;
  }

  // Group by client
  const grouped = {};
  data.entries.forEach(e => {
    if (!grouped[e.client_name]) grouped[e.client_name] = [];
    grouped[e.client_name].push(e);
  });

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="overview-table">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Task/Service</th>
                <th className="text-left px-4 py-3">Employee</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-right px-4 py-3">Hours</th>
                <th className="text-right px-4 py-3">Rate/Hr</th>
                <th className="text-right px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Object.entries(grouped).map(([clientName, entries]) => (
                entries.map((e, i) => (
                  <tr key={`${e.task_id}-${e.date}-${e.employee_id}-${i}`} className="hover:bg-slate-50" data-testid={`overview-row-${i}`}>
                    {i === 0 ? (
                      <td className="px-4 py-2.5 font-medium text-slate-900 align-top" rowSpan={entries.length}>{clientName}</td>
                    ) : null}
                    <td className="px-4 py-2.5 text-slate-700">{e.service_name}</td>
                    <td className="px-4 py-2.5 text-slate-600">{e.employee_name}</td>
                    <td className="px-4 py-2.5 text-slate-500">{e.date}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-700">{e.hours.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {e.rate_defined ? (
                        <span className="font-mono text-slate-700">₹{e.rate_per_hour}</span>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">NOT SET</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold text-slate-900">
                      {e.rate_defined ? `₹${e.amount.toLocaleString('en-IN')}` : '-'}
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// --- Client Report View (task-wise breakdown) ---
function ClientReportView({ data }) {
  if (!data?.breakdown?.length) {
    return <Card className="border-slate-200"><CardContent className="py-12 text-center text-slate-400">No billing data found for this client</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="client-report-view">
      {data.client && (
        <div className="mb-2">
          <h2 className="text-xl font-bold text-slate-900">{data.client.company}</h2>
        </div>
      )}
      {data.breakdown.map((item, idx) => (
        <Card key={item.service_id || item.service_name || idx} className={`border-slate-200 shadow-sm ${!item.rate_defined ? 'border-l-4 border-l-amber-400' : ''}`} data-testid={`breakdown-${idx}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CardTitle className="text-base font-semibold">{item.service_name}</CardTitle>
                {!item.rate_defined && <Badge className="bg-amber-100 text-amber-700 text-xs">RATE NOT SET</Badge>}
                {!item.is_billable && <Badge className="bg-slate-100 text-slate-500 text-xs">Non-Billable</Badge>}
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">Rate: <strong className="text-slate-900">{item.rate_defined ? `₹${item.rate_per_hour}/hr` : 'Not Set'}</strong></span>
                <span className="text-slate-500">Hours: <strong className="text-slate-900">{item.total_hours}</strong></span>
                <span className="text-slate-500">Amount: <strong className="text-emerald-600">₹{item.total_amount.toLocaleString('en-IN')}</strong></span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {item.employees.map((emp, ei) => (
                <div key={emp.id || emp.name || ei} className="flex items-center justify-between px-5 py-2.5 text-sm" data-testid={`emp-${idx}-${ei}`}>
                  <span className="text-slate-700 font-medium">{emp.name}</span>
                  <div className="flex items-center gap-6">
                    <span className="text-slate-500 font-mono">{emp.hours.toFixed(2)}h</span>
                    <span className="font-mono font-semibold text-slate-900 min-w-[80px] text-right">₹{emp.amount.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


// --- Rate Management Dialog ---
function RateDialog({ open, onClose, clientId, clientName, services, rates, onSave }) {
  const [editRates, setEditRates] = useState([]);

  useEffect(() => {
    if (open) {
      // Merge existing rates with available services
      const allServiceNames = new Set();
      services.forEach(s => {
        // Get activities for each service
        allServiceNames.add(s.name);
      });

      const rateMap = {};
      rates.forEach(r => { rateMap[r.service_name] = r; });

      const merged = [...allServiceNames].map(sn => ({
        service_name: sn,
        rate_per_hour: rateMap[sn]?.rate_per_hour ?? '',
        is_billable: rateMap[sn]?.is_billable ?? true,
      }));

      // Also add any rates for services not in the master list
      rates.forEach(r => {
        if (!allServiceNames.has(r.service_name)) {
          merged.push({
            service_name: r.service_name,
            rate_per_hour: r.rate_per_hour,
            is_billable: r.is_billable,
          });
        }
      });

      setEditRates(merged);
    }
  }, [open, services, rates]);

  const updateRate = (idx, field, value) => {
    setEditRates(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const handleSave = () => {
    const toSave = editRates
      .filter(r => r.rate_per_hour !== '' && r.rate_per_hour !== null)
      .map(r => ({
        service_name: r.service_name,
        rate_per_hour: parseFloat(r.rate_per_hour) || 0,
        is_billable: r.is_billable,
      }));
    onSave(toSave);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="rate-dialog">
        <DialogHeader>
          <DialogTitle>Manage Rates - {clientName}</DialogTitle>
          <DialogDescription className="sr-only">Set hourly rates for each service</DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          <table className="w-full text-sm" data-testid="rate-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase">
                <th className="text-left py-2 pr-3">Service</th>
                <th className="text-right py-2 px-3 w-32">Rate/Hr (₹)</th>
                <th className="text-center py-2 px-3 w-24">Billable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {editRates.map((r, i) => (
                <tr key={r.service_name} data-testid={`rate-row-${i}`}>
                  <td className="py-2.5 pr-3 font-medium text-slate-900">{r.service_name}</td>
                  <td className="py-2.5 px-3">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={r.rate_per_hour}
                      onChange={e => updateRate(i, 'rate_per_hour', e.target.value)}
                      className="text-right w-full"
                      data-testid={`rate-input-${i}`}
                    />
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <Switch
                      checked={r.is_billable}
                      onCheckedChange={v => updateRate(i, 'is_billable', v)}
                      data-testid={`billable-switch-${i}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#5B6FD6] hover:bg-[#4a5bc2]" onClick={handleSave} data-testid="save-rates-btn">Save Rates</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
